import type { Parent, PhrasingContent, Root, Text } from "mdast";
import type { Plugin } from "unified";
import { SKIP, visit } from "unist-util-visit";

type MdastNode = PhrasingContent | { type: "html"; value: string };

// A text node's value is already decoded (`&lt;b` → `<b`); re-escape it before
// splicing into raw HTML, or rehypeRaw reads it as markup.
const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Remark plugin to support underline syntax: ++underlined text++
 * This matches the syntax used by @tiptap/markdown for underline marks.
 *
 * Transforms ++text++ into <u>text</u> HTML elements.
 *
 * Handles two cases:
 * 1. Simple: ++plain text++ - matched within a single text node
 * 2. Complex: ++**bold text**++ - where ++ markers are in separate text nodes
 *    surrounding other inline elements like strong, emphasis, etc.
 */
export const remarkUnderline: Plugin<[], Root> = () => {
  return (tree: Root) => {
    // First pass: handle simple cases within single text nodes
    visit(tree, "text", (node: Text, index, parent) => {
      if (!parent || index === undefined) return;

      const value = node.value;
      // Match ++text++ pattern (non-greedy, handles multiple on same line)
      const regex = /\+\+([^+]+)\+\+/g;

      if (!regex.test(value)) return;

      // Reset regex lastIndex after test
      regex.lastIndex = 0;

      const children: (Text | { type: "html"; value: string })[] = [];
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(value)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          children.push({
            type: "text",
            value: value.slice(lastIndex, match.index),
          });
        }

        // Add the underlined content as HTML
        children.push({
          type: "html",
          value: `<u>${escapeHtml(match[1])}</u>`,
        });

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text after last match
      if (lastIndex < value.length) {
        children.push({
          type: "text",
          value: value.slice(lastIndex),
        });
      }

      // Replace the current node with the new children
      if (children.length > 0) {
        parent.children.splice(index, 1, ...children);
        return [SKIP, index + children.length];
      }
    });

    // Second pass: handle complex cases where ++ surrounds other elements
    // e.g., ++**bold**++ becomes <u><strong>bold</strong></u>
    visit(tree, (node) => {
      if (!("children" in node)) return;
      const parent = node as Parent;
      const children = parent.children as MdastNode[];

      let i = 0;
      while (i < children.length) {
        const child = children[i];

        // Look for text node that contains opening ++
        if (child.type === "text") {
          const startText = child.value;

          // Check if this text ends with ++ (opening marker for next element)
          // e.g., "some text++" or just "++"
          if (startText.endsWith("++")) {
            // Find the matching closing ++ in a subsequent text node
            let j = i + 1;
            while (j < children.length) {
              const nextChild = children[j];
              if (nextChild.type === "text") {
                const nextValue = nextChild.value;
                if (nextValue.startsWith("++")) {
                  // Found closing ++
                  // Extract content before and after the ++ markers
                  const beforeMarker =
                    startText.length > 2 ? startText.slice(0, -2) : "";
                  const afterMarker =
                    nextValue.length > 2 ? nextValue.slice(2) : "";

                  // Collect nodes between start and end
                  const innerNodes = children.slice(i + 1, j);

                  // Build new children array
                  const newChildren: MdastNode[] = [];

                  // Add text before ++ if any
                  if (beforeMarker) {
                    newChildren.push({ type: "text", value: beforeMarker });
                  }

                  // Add opening <u> tag
                  newChildren.push({ type: "html", value: "<u>" });

                  // Add inner content
                  newChildren.push(...innerNodes);

                  // Add closing </u> tag
                  newChildren.push({ type: "html", value: "</u>" });

                  // Add text after ++ if any
                  if (afterMarker) {
                    newChildren.push({ type: "text", value: afterMarker });
                  }

                  // Replace the range [i, j] with newChildren
                  children.splice(i, j - i + 1, ...newChildren);

                  // Don't increment i, check the same position again
                  // in case there are nested or adjacent underlines
                  break;
                }
              }
              j++;
            }
          }
        }
        i++;
      }
    });
  };
};
